<script setup lang="ts">
  import ArtificialIntelligence2 from '@/components/svg/Illustrations/ArtificialIntelligence2.vue'
  import { Button } from '@/components/ui/button'
  import { useTranslation } from '@/composables/useTranslation'
  import { useUserStore } from '@/shared/stores/userStore'
  import { UsersService } from '@/types/generated'
  import { computed, ref } from 'vue'

  const emit = defineEmits<{
    introAccepted: []
  }>()

  const { t } = useTranslation({
    en: {
      greeting: 'Hello, {userName}!',
      capabilities:
        'In this space, you can start general conversations, upload documents, organize them into folders, and ask questions.',
      disclaimer:
        "This workspace is separate from your specific law cases, so I can't see case details here. For that, please use the AI chat inside a specific case.",
      iUnderstand: 'I understand',
      loading: 'Loading...'
    },
    de: {
      greeting: 'Hallo, {userName}!',
      capabilities:
        'In diesem Bereich können Sie allgemeine Unterhaltungen starten, Dokumente hochladen, diese in Ordnern organisieren und Fragen stellen.',
      disclaimer:
        'Dieser Arbeitsbereich ist von Ihren konkreten Mandatsfällen getrennt, daher kann ich hier keine Falldetails sehen. Bitte nutzen Sie für fallbezogene Fragen den KI-Chat innerhalb des jeweiligen Falls.',
      iUnderstand: 'Ich verstehe',
      loading: 'Laden...'
    }
  })

  const userStore = useUserStore()
  const isLoading = ref(false)

  const userName = computed(() => {
    if (!userStore.user) return ''
    const firstName = userStore.user.first_name || ''
    const lastName = userStore.user.last_name || ''
    return `${firstName} ${lastName}`.trim() || 'User'
  })

  const greetingText = computed(() => {
    return t('greeting', { userName: userName.value })
  })

  const handleAccept = async () => {
    if (!userStore.user) return

    isLoading.value = true
    try {
      await UsersService.usersPartialUpdate({
        uuid: userStore.user.uuid,
        requestBody: {
          global_ai_chat_intro_seen: true
        }
      })

      // Update local user store
      if (userStore.user) {
        userStore.user.global_ai_chat_intro_seen = true
      }

      emit('introAccepted')
    } catch (error) {
      console.error('Failed to mark intro as seen:', error)
    } finally {
      isLoading.value = false
    }
  }
</script>

<template>
  <div class="flex h-full w-full items-center justify-center bg-gray-50">
    <div class="flex items-start gap-[30px]">
      <!-- Illustration - AI robot with screens -->
      <div class="relative h-[288px] w-[288px] shrink-0">
        <ArtificialIntelligence2 />
      </div>

      <!-- Text content and button -->
      <div class="flex w-[400px] shrink-0 flex-col items-center gap-4">
        <!-- Chat messages -->
        <div class="flex w-full flex-col gap-2">
          <!-- Greeting message -->
          <div class="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4">
            <p class="text-sm font-normal leading-5 text-gray-800">
              {{ greetingText }}
            </p>
          </div>

          <!-- Capabilities message -->
          <div
            class="flex w-full items-center gap-4 rounded-lg border border-gray-200 bg-white p-4"
          >
            <p class="h-full w-[368px] shrink-0 text-sm font-normal leading-5 text-gray-800">
              {{ t('capabilities') }}
            </p>
          </div>

          <!-- Disclaimer message -->
          <div
            class="flex w-[362px] shrink-0 items-center gap-4 rounded-lg border border-gray-200 bg-white p-4"
          >
            <p class="grow basis-0 text-sm font-normal leading-5 text-gray-800">
              {{ t('disclaimer') }}
            </p>
          </div>
        </div>

        <!-- I understand button -->
        <Button
          :disabled="isLoading"
          class="h-10 w-full rounded-lg px-2 py-2 text-base font-medium shadow-sm"
          @click="handleAccept"
        >
          {{ isLoading ? t('loading') : t('iUnderstand') }}
        </Button>
      </div>
    </div>
  </div>
</template>
